import React, { useState } from 'react';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PanelEmptyState from '../../Components/PanelEmptyState';
import { colors } from '../../styles/theme';
import { useMarketComparison } from '../hooks/useMarketComparison';
import { describeMarketCard } from '../marketComparison';
import { normalizeMarketCenter } from '../marketComparablesMap';
import MarketComparisonDialog from './MarketComparisonDialog';
import {
  MARKET_FONT,
  MarketBar,
  MarketBarLegend,
  VerdictChip,
  marketLinkButtonSx,
  marketOutlineButtonSx,
  marketSkeletonSx,
  marketSmallSx,
  marketTextSx,
} from './MarketParts';

export const MARKET_CARD_TITLE = 'Precio vs. mercado';
/** Alto fijo del cuerpo de la tarjeta: igual en carga, veredicto, avisos y error (sin saltos). */
export const MARKET_CARD_BODY_HEIGHT = 132;

const titleSx = {
  fontFamily: MARKET_FONT,
  fontSize: '13px',
  fontWeight: 600,
  color: colors.textPanelTitle,
  lineHeight: '16px',
  mb: 1,
};
const bodySx = {
  height: MARKET_CARD_BODY_HEIGHT,
  boxSizing: 'border-box',
  bgcolor: colors.backgroundSurface,
  border: '1px solid #3A3A3A',
  borderRadius: '10px',
  p: 1.5,
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
  overflow: 'hidden',
};
const footerSx = {
  mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, minHeight: 28,
};

// Muestra baja: vacío mini más compacto (gap 4 px) para que entre el link en los 132 px.
const lowSampleEmptySx = { flex: 'none', gap: 0.5 };
const lowSampleLinkSx = { ...marketLinkButtonSx, height: 24, mt: 0.5 };
// Sin veredicto (falta o dato dudoso, t1064u): el mismo vacío mini, con «Editar unidad» y/o
// «Ver informe ›» debajo (24 px, como el link de muestra baja).
const reasonActionsSx = { display: 'flex', justifyContent: 'center', gap: 1, mt: 0.5 };
const reasonEditSx = { ...marketOutlineButtonSx, height: 24 };
const reasonReportSx = { ...marketLinkButtonSx, height: 24 };

function CardSkeleton() {
  return (
    <Box sx={bodySx} data-testid="market-card-loading" aria-busy="true" aria-label="Cargando comparación de mercado">
      <Skeleton variant="rounded" width={110} height={24} sx={{ ...marketSkeletonSx, borderRadius: '999px' }} />
      <Skeleton variant="rounded" height={8} sx={{ ...marketSkeletonSx, my: 0.75 }} />
      <Skeleton variant="rounded" height={12} width="70%" sx={marketSkeletonSx} />
      <Skeleton variant="rounded" height={14} width="50%" sx={{ ...marketSkeletonSx, mt: 'auto' }} />
    </Box>
  );
}

/**
 * «Precio vs. mercado» en la ficha (UF, lote catastral y parcela de showroom). Pide al
 * montar (con caché por registro, dueño y filtros), alto fijo en todos los estados y se
 * esconde sola con el flag apagado (Front o `features.market_comparison`), 401, 403 o 404.
 * «Ver informe ›» abre el modal «Comparación de mercado» (reusa la respuesta en caché).
 * Ver docs/maps/market-comparison.md.
 *
 * @param {{ itemKind: 'user_lot'|'cadastral_lot'|'cad_parcel', recordId: string,
 *   ownerUserId?: string|null, revision?: string|number, unitLabel?: string,
 *   onEdit?: () => void, center?: {lat:number,lng:number}|null,
 *   onShowOnMap?: (view: object) => void }} props
 * `onEdit`: abre la edición de la unidad (solo si quien mira puede editar).
 * `unitLabel`: nombre de la unidad para el subtítulo del informe («Depto 3B»).
 * `center` + `onShowOnMap`: habilitan «Ver en el mapa» en Comparables (modo Comparables del
 * mapa principal). `onShowOnMap` recibe `{ itemKind, recordId, ownerUserId, revision,
 * unitLabel, center, radiusM, months, operation }`.
 */
export default function MarketComparisonCard({
  itemKind = 'user_lot',
  recordId,
  ownerUserId = null,
  revision = '',
  unitLabel = '',
  onEdit = undefined,
  center = null,
  onShowOnMap = undefined,
}) {
  const { status, data, reload } = useMarketComparison({ itemKind, recordId, ownerUserId, revision });
  // `false` o la pestaña con la que abre el informe ('resumen' | 'comparables').
  const [reportOpen, setReportOpen] = useState(false);
  const safeCenter = normalizeMarketCenter(center);
  if (status === 'hidden') return null;

  let body;
  let card = null;
  if (status === 'loading') {
    body = <CardSkeleton />;
  } else if (status === 'error' || !data) {
    body = (
      <Box sx={bodySx} data-testid="market-card-error">
        <Typography sx={marketTextSx}>No pudimos cargar la comparación.</Typography>
        <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'flex-end' }}>
          <Button size="small" onClick={reload} sx={marketOutlineButtonSx}>Reintentar</Button>
        </Box>
      </Box>
    );
  } else {
    card = describeMarketCard(data);
    const reportButton = card.hasReport ? (
      <Button size="small" onClick={() => setReportOpen('resumen')} sx={marketLinkButtonSx}>Ver informe ›</Button>
    ) : null;
    if (card.kind === 'verdict') {
      body = (
        <Box sx={bodySx} data-testid="market-card-verdict">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <VerdictChip verdict={card.verdict} label={card.chip} />
            <Typography sx={{ ...marketSmallSx, color: 'rgba(255,255,255,0.6)' }} data-testid="market-card-diff">
              {card.diffText}
            </Typography>
          </Box>
          <MarketBar bar={card.bar} verdict={card.verdict} />
          {card.monthly ? (
            <MarketBarLegend
              rangeLabel={card.rangeShortLabel}
              rangePrefix="Esperable"
              rangeTitle={card.areaLabel}
              unitLabel={card.unitLabel}
              fit
            />
          ) : <MarketBarLegend rangeLabel={card.rangeLabel} unitLabel={card.unitLabel} />}
          <Box sx={footerSx}>
            <Typography sx={marketSmallSx} noWrap title={card.basisNote || undefined} data-testid="market-card-footer">
              {card.footer}
            </Typography>
            {reportButton}
          </Box>
        </Box>
      );
    } else if (card.kind === 'low_sample') {
      // Muestra baja (1 a 4): mismo vacío mini, texto de muestra baja y «Ver los N ›», que abre
      // el informe en Comparables (no hay Resumen con mediana).
      body = (
        <Box sx={{ ...bodySx, justifyContent: 'center' }} data-testid="market-card-low_sample">
          <PanelEmptyState
            dense
            icon={SearchIcon}
            title={card.title}
            hint={card.text}
            sx={lowSampleEmptySx}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button size="small" onClick={() => setReportOpen('comparables')} sx={lowSampleLinkSx} data-testid="market-card-low-sample-action">
              {card.actionLabel}
            </Button>
          </Box>
        </Box>
      );
    } else if (card.kind === 'no_data') {
      // Pocos comparables: estado vacío mini, centrado y sin chip (no parece un veredicto).
      body = (
        <Box sx={{ ...bodySx, justifyContent: 'center' }} data-testid="market-card-no_data">
          <PanelEmptyState dense icon={SearchIcon} title={card.title} hint={card.text} sx={{ flex: 'none' }} />
        </Box>
      );
    } else {
      // Falta un dato, dato dudoso u otro motivo: vacío mini sin chip, un solo aviso.
      const showEdit = card.canEdit && typeof onEdit === 'function';
      body = (
        <Box sx={{ ...bodySx, justifyContent: 'center' }} data-testid={`market-card-${card.kind}`}>
          <PanelEmptyState
            dense
            icon={card.icon === 'edit' ? EditOutlinedIcon : ErrorOutlineIcon}
            title={card.title}
            hint={card.text}
            sx={lowSampleEmptySx}
          />
          {showEdit || card.hasReport ? (
            <Box sx={reasonActionsSx}>
              {showEdit ? <Button size="small" onClick={onEdit} sx={reasonEditSx}>Editar unidad</Button> : null}
              {card.hasReport ? (
                <Button size="small" onClick={() => setReportOpen('resumen')} sx={reasonReportSx}>Ver informe ›</Button>
              ) : null}
            </Box>
          ) : null}
        </Box>
      );
    }
  }

  return (
    <Box data-testid="market-comparison-card" sx={{ flexShrink: 0 }}>
      <Typography sx={titleSx}>{MARKET_CARD_TITLE}</Typography>
      {body}
      {reportOpen ? (
        <MarketComparisonDialog
          itemKind={itemKind}
          recordId={recordId}
          ownerUserId={ownerUserId}
          revision={revision}
          unitLabel={unitLabel}
          onEdit={onEdit}
          onClose={() => setReportOpen(false)}
          initialTab={reportOpen === 'comparables' ? 'comparables' : 'resumen'}
          onShowOnMap={safeCenter && onShowOnMap ? (view) => onShowOnMap({
            itemKind, recordId, ownerUserId, revision, unitLabel, center: safeCenter, ...view,
          }) : undefined}
        />
      ) : null}
    </Box>
  );
}
